import { gqlTypes } from '@/types/gqlTypes';
import { useMutation } from '@apollo/client';
import { registerThirdPartyChip, resellerChipDetailsQuery } from '../ResellerChipDetails/ResellerChipDetails.graphql';
import ModalAlert from '../components/modals/ModalAlert';
import ModalActions from '../components/modals/ModalActions/ModalActions';
import { useRef } from 'react';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { AAHASearchUrl } from '@/lib/constants';
import { viewingChipsQuery } from './ViewingChips.graphql';

const PAGE_SIZE = 20;
const OFFSET = 0;

export function RegisterThirdPartyChipModal({
  serialNumber,
  onClose,
}: {
  serialNumber: string;
  onClose: (success?: boolean) => void;
}) {
  const [registerChip, { error, loading, called }] = useMutation<
    gqlTypes.resellerRegisterThirdPartyChip,
    gqlTypes.resellerRegisterThirdPartyChipVariables
  >(registerThirdPartyChip, {
    refetchQueries: [
      { query: resellerChipDetailsQuery, variables: { id: serialNumber } },
      // A freshly registered third-party chip has no contact info or transfer, so it lands in Stock.
      {
        query: viewingChipsQuery,
        variables: { limit: PAGE_SIZE, offset: OFFSET, statusType: gqlTypes.ChipStatusType.SHIPPED },
      },
    ],
    awaitRefetchQueries: true,
  });

  const ref = useRef<PopupActions>(null);

  if (error) {
    return (
      <ModalAlert message="There was an error registering this chip, please try again." open={true} onClose={onClose} />
    );
  }

  return (
    <Popup open={true} modal contentStyle={{ padding: '0', fontSize: '26px' }} ref={ref} onClose={() => onClose()}>
      <div style={{ padding: '2em', fontSize: '0.8625em' }}>
        Would you like to add your organization's information to this chip and include it in your stock list for future
        registration?
        <br />
        <br /> You can also{' '}
        <a href={AAHASearchUrl(serialNumber)} target="_blank" rel="noopener noreferrer">
          check other registries{' '}
          <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 19H5V5H12V3H3V21H21V12H19V19ZM14 3V5H17.59L7.76 14.83L9.17 16.24L19 6.41V10H21V3H14Z"></path>
          </svg>
        </a>
      </div>
      <form>
        <ModalActions
          actions={[
            {
              text: 'Add to stock',
              primary: true,
              onClick: async () => {
                if (loading || called) {
                  return;
                }
                try {
                  const result = await registerChip({ variables: { input: { serialNumber } } });
                  onClose(!!result);
                } catch {
                  // Leave the modal open — the `error` branch above renders the alert.
                }
              },
            },
            {
              text: 'Cancel',
              onClick: () => onClose(false),
            },
          ]}
        />
      </form>
    </Popup>
  );
}
